import type { Injector } from '@angular/core';
import { ActivityEntityName, ItemDeletedResponse, JobItemCommandId } from '@jms/model';
import { EntityDataServiceRaw } from '@workbuddy/services/entities';
import { ConfirmServicePro } from '@workbuddy/ui/blocks/confirm';
import { lastValueFrom } from 'rxjs';
import { resolveContractorBulkLines, runContractorBulkLines } from './contractor.item.bulk.tally';

/** The command id the contractor board's bulk list carries and its host answers to. */
export const CONTRACTOR_ITEM_DELETE_BULK_ACTION = 'items.contractor.delete';

export interface ContractorBulkCommand {
  readonly action: string;
  readonly data: unknown[];
}

export interface ContractorBulkOutcome {
  readonly handled: boolean;
  readonly clearSelection?: boolean;
}

/**
 * Deletes the selected lines one DeleteItem at a time (WBS-11932).
 *
 * The board's bulk pipeline runs on the server under the Jobs Manage right, which a subcontractor
 * never holds, so the portal host answers this command itself. Every ticked line is accounted for: one the
 * subcontractor may not delete is left out with the reason the server stamped on it, and the report names every
 * line not deleted, one line per reason (Hung 2026-10-08, WBS-12406).
 */
export async function handleContractorItemDeleteBulkCommand(args: ContractorBulkCommand, injector: Injector, itemsChanged?: () => void): Promise<ContractorBulkOutcome> {

  if (args?.action !== CONTRACTOR_ITEM_DELETE_BULK_ACTION) return { handled: false };

  const lines = resolveContractorBulkLines(args.data);
  if (!lines.length) return { handled: true };

  const eligible = lines.filter(line => line.capabilities?.canDelete === true).length;
  const skipped = lines.length - eligible;
  if (eligible) {
    const question = `Delete ${eligible} item${eligible === 1 ? '' : 's'}? Any tasks on them go with them.${skipped ? ` ${skipped} of the ${lines.length} selected can't be deleted — they are listed after.` : ''}`;
    const confirmed = await injector.get(ConfirmServicePro).confirm('Delete items', question, 'danger');
    if (!confirmed) return { handled: true };
  }

  const entityData = injector.get(EntityDataServiceRaw);

  // Sequential (inside the tally): each delete rewrites the stage's pending array, and two in flight would race on the same document.
  await runContractorBulkLines(lines, injector, async line => {
    if (line.capabilities?.canDelete !== true) return { kind: 'unchanged', reason: line.capabilities?.deleteRefusal ?? "This line can't be deleted" };

    const result = await lastValueFrom(entityData.executeCommand<ItemDeletedResponse>(ActivityEntityName.JobItem, JobItemCommandId.DeleteItem, { itemId: line.itemId, jobId: line.jobId, stageId: line.stageId }));
    return result && result.success === false ? { kind: 'failed', reason: result.error ?? 'Not deleted' } : { kind: 'updated' };
  }, itemsChanged);

  return { handled: true, clearSelection: true };
}
